import {
    AppShell,
    Box,
    Burger,
    Group,
    Image,
    NavLink,
    Stack,
    Text,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import {useDisclosure, useLocalStorage} from '@mantine/hooks';
import React, {useEffect, useMemo, useState} from 'react';
import {Link, Navigate, Route, Routes, useLocation} from 'react-router-dom';
import {
    IconArchive,
    IconFileTypePdf,
    IconGraph,
    IconLayoutSidebarLeftCollapse,
    IconLayoutSidebarLeftExpand,
    IconList,
    IconNews,
    IconQrcode,
    IconSettings,
    IconUserStar,
    IconUsers,
} from '@tabler/icons-react';
import {useQuery} from '@tanstack/react-query';
import {jwtDecode} from 'jwt-decode';
import {UserService} from '../../client';
import {useAuthStore} from '../../store/authStore';
import {
    canAccessDocuments,
    canAccessPath,
    canManageSiteSettings,
    canManageUsers,
    getNavItemsForRole,
    HOME_PATH_BY_ROLE,
    normalizeRole,
    ROLE_LABELS,
    SETTINGS_NAV_ITEM,
    USERS_NAV_ITEM,
    type UserRole,
} from '../../utils/roles';
import {Archive} from '../archive/Archive';
import {CheckIn} from '../check-in/CheckIn';
import {Documents} from '../documents/Documents';
import Login from '../login/Login';
import {Newsletter} from '../newsletter/Newsletter';
import {PasskeyPage} from '../passkey/PasskeyPage';
import {Registrations} from '../registrations/Registrations';
import {Settings} from '../settings/Settings';
import {Staff} from '../staff/Staff';
import {Statistics} from '../statistics/Statistics';
import {Users} from '../users/Users';
import {UserAccountMenu} from './UserAccountMenu';

const SIDEBAR_STORAGE_KEY = 'pkc-admin-sidebar-collapsed';
const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 72;

const NAV_ICONS: Record<string, React.ReactNode> = {
    registrations: <IconList size="1.1rem" stroke={1.5}/>,
    'check-in': <IconQrcode size="1.1rem" stroke={1.5}/>,
    staff: <IconUserStar size="1.1rem" stroke={1.5}/>,
    newsletter: <IconNews size="1.1rem" stroke={1.5}/>,
    documents: <IconFileTypePdf size="1.1rem" stroke={1.5}/>,
    statistics: <IconGraph size="1.1rem" stroke={1.5}/>,
    archive: <IconArchive size="1.1rem" stroke={1.5}/>,
    users: <IconUsers size="1.1rem" stroke={1.5}/>,
    settings: <IconSettings size="1.1rem" stroke={1.5}/>,
};

type NavItem = {
    href: string;
    label: string;
    icon: React.ReactNode;
};

function SidebarNavItem({
    item,
    collapsed,
    active,
}: {
    item: NavItem;
    collapsed: boolean;
    active: boolean;
}) {
    const link = (
        <NavLink
            component={Link}
            to={item.href}
            label={collapsed ? undefined : item.label}
            leftSection={item.icon}
            active={active}
            variant="subtle"
            styles={{
                section: collapsed ? {marginInlineEnd: 0} : undefined,
                body: collapsed ? {display: 'none'} : undefined,
            }}
        />
    );

    if (collapsed) {
        return (
            <Tooltip label={item.label} position="right" withArrow>
                {link}
            </Tooltip>
        );
    }

    return link;
}

function RoleRouteGuard({
    role,
    children,
}: {
    role: UserRole;
    children: React.ReactNode;
}) {
    const location = useLocation();
    if (!canAccessPath(role, location.pathname)) {
        return <Navigate to={HOME_PATH_BY_ROLE[role]} replace/>;
    }
    return <>{children}</>;
}

export function Shell() {
    const [mobileOpened, {toggle: toggleMobile}] = useDisclosure();
    const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage<boolean>({
        key: SIDEBAR_STORAGE_KEY,
        defaultValue: false,
    });
    const location = useLocation();
    const isLoggedIn = useAuthStore(state => state.isLoggedIn);
    const token = useAuthStore(state => state.token);
    const [authHydrated, setAuthHydrated] = useState(() => useAuthStore.persist.hasHydrated());

    useEffect(() => {
        const unsub = useAuthStore.persist.onFinishHydration(() => setAuthHydrated(true));
        setAuthHydrated(useAuthStore.persist.hasHydrated());
        return unsub;
    }, []);

    const {data: currentUserProfile, isLoading: isProfileLoading} = useQuery({
        queryKey: ['current-user'],
        queryFn: UserService.getCurrentUserApiV1UserMeGet,
        enabled: isLoggedIn,
    });

    const role = normalizeRole(currentUserProfile?.role);
    const showUsersNav = canManageUsers(role);
    const showSettingsNav = canManageSiteSettings(role);

    const navItems = useMemo(
        () =>
            getNavItemsForRole(role).map((item) => ({
                href: item.href,
                label: item.label,
                icon: NAV_ICONS[item.key],
            })),
        [role],
    );

    const displayInitials = useMemo(() => {
        if (!token) {
            return '?';
        }
        try {
            const username = jwtDecode<{sub?: string}>(token).sub ?? '';
            const parts = username.split(/[._-]/).filter(Boolean);
            if (parts.length >= 2) {
                return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
            }
            return username.slice(0, 2).toUpperCase() || '?';
        } catch {
            return '?';
        }
    }, [token]);

    if (!authHydrated) {
        return null;
    }

    if (!isLoggedIn) {
        return <Login/>;
    }

    if (isProfileLoading && !currentUserProfile) {
        return null;
    }

    const isActive = (href: string) => {
        if (href === '/registrations') {
            return location.pathname === '/' || location.pathname === '/registrations';
        }
        return location.pathname === href || location.pathname.startsWith(`${href}/`);
    };

    const navbarWidth = sidebarCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;
    const homePath = HOME_PATH_BY_ROLE[role];

    return (
        <AppShell
            header={{height: 56}}
            navbar={{
                width: navbarWidth,
                breakpoint: 'sm',
                collapsed: {mobile: !mobileOpened, desktop: false},
            }}
            padding="md"
        >
            <AppShell.Header>
                <Group h="100%" px="md" justify="space-between">
                    <Group gap="sm">
                        <Burger
                            opened={mobileOpened}
                            onClick={toggleMobile}
                            hiddenFrom="sm"
                            size="sm"
                        />
                        <UnstyledButton
                            visibleFrom="sm"
                            onClick={() => setSidebarCollapsed((value) => !value)}
                            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        >
                            {sidebarCollapsed ? (
                                <IconLayoutSidebarLeftExpand size={20} stroke={1.5}/>
                            ) : (
                                <IconLayoutSidebarLeftCollapse size={20} stroke={1.5}/>
                            )}
                        </UnstyledButton>
                    </Group>
                </Group>
            </AppShell.Header>
            <AppShell.Navbar p={sidebarCollapsed ? 'xs' : 'md'}>
                <Stack h="100%" justify="space-between" gap="md">
                    <Stack gap="md">
                        <Group justify={sidebarCollapsed ? 'center' : 'flex-start'} px={sidebarCollapsed ? 0 : 'xs'}>
                            {sidebarCollapsed ? (
                                <Image w={36} src="/logo.png" alt="Parkour Camp"/>
                            ) : (
                                <Group gap="sm">
                                    <Image w={40} src="/logo.png" alt="Parkour Camp"/>
                                    <Box>
                                        <Text size="sm" fw={600} lh={1.2}>Parkour Camp</Text>
                                        <Text size="xs" c="dimmed">Admin</Text>
                                    </Box>
                                </Group>
                            )}
                        </Group>
                        <Stack gap={0}>
                            {navItems.map((item) => (
                                <SidebarNavItem
                                    key={item.href}
                                    item={item}
                                    collapsed={sidebarCollapsed}
                                    active={isActive(item.href)}
                                />
                            ))}
                        </Stack>
                    </Stack>
                    <Stack gap="sm">
                        {showUsersNav && (
                            <SidebarNavItem
                                item={{
                                    href: USERS_NAV_ITEM.href,
                                    label: USERS_NAV_ITEM.label,
                                    icon: NAV_ICONS[USERS_NAV_ITEM.key],
                                }}
                                collapsed={sidebarCollapsed}
                                active={isActive(USERS_NAV_ITEM.href)}
                            />
                        )}
                        {showSettingsNav && (
                            <SidebarNavItem
                                item={{
                                    href: SETTINGS_NAV_ITEM.href,
                                    label: SETTINGS_NAV_ITEM.label,
                                    icon: NAV_ICONS[SETTINGS_NAV_ITEM.key],
                                }}
                                collapsed={sidebarCollapsed}
                                active={isActive(SETTINGS_NAV_ITEM.href)}
                            />
                        )}
                        <UserAccountMenu
                            variant="sidebar"
                            collapsed={sidebarCollapsed}
                            initials={displayInitials}
                            role={role}
                            roleLabel={ROLE_LABELS[role]}
                        />
                    </Stack>
                </Stack>
            </AppShell.Navbar>
            <AppShell.Main>
                <RoleRouteGuard role={role}>
                    <Routes>
                        {(role === 'admin' || role === 'sani' || role === 'operator') && (
                            <>
                                <Route path="/" element={<Registrations/>}/>
                                <Route path="/registrations" element={<Registrations/>}/>
                            </>
                        )}
                        {(role === 'admin' || role === 'operator' || role === 'check_in') && (
                            <Route path="/check-in" element={<CheckIn/>}/>
                        )}
                        {(role === 'admin' || role === 'operator') && (
                            <>
                                <Route path="/staff" element={<Staff/>}/>
                                <Route path="/newsletter" element={<Newsletter/>}/>
                            </>
                        )}
                        <Route path="/statistics" element={<Statistics/>}/>
                        <Route path="/archive" element={<Archive/>}/>
                        {canManageUsers(role) && <Route path="/users" element={<Users/>}/>}
                        {canAccessDocuments(role) && (
                            <Route path="/documents" element={<Documents/>}/>
                        )}
                        {canManageSiteSettings(role) && (
                            <Route path="/settings" element={<Settings/>}/>
                        )}
                        <Route path="/passkey" element={<PasskeyPage/>}/>
                        <Route path="*" element={<Navigate to={homePath} replace/>}/>
                    </Routes>
                </RoleRouteGuard>
            </AppShell.Main>
        </AppShell>
    );
}
